// ============================================================================
// Kalkulator: BMI, BMR (Mifflin-St Jeor) i TDEE.
//
// BMI  = masa(kg) / visina(m)^2
// BMR  = 10*masa + 6.25*visina(cm) - 5*godine + (5 za muškarce, -161 za žene)
// TDEE = BMR * faktor aktivnosti
//
// Formule su standardne (Mifflin-St Jeor je danas najtačnija za zdrave odrasle).
// ============================================================================

// Faktori aktivnosti - vrednost je množilac za TDEE
export const ACTIVITY_LEVELS = [
    { value: '1.2', label: 'Sedentarno (bez vežbanja)' },
    { value: '1.375', label: 'Lako aktivno (1-3x nedeljno)' },
    { value: '1.55', label: 'Umereno aktivno (3-5x nedeljno)' },
    { value: '1.725', label: 'Vrlo aktivno (6-7x nedeljno)' },
    { value: '1.9', label: 'Ekstremno aktivno (fizički posao / 2x dnevno)' },
];

// Pretvara "1,83" ili "183" ili "183cm" u broj. Vraća null ako nije broj.
const toNumber = (value) => {
    if (value === null || value === undefined) return null;
    const cleaned = String(value).replace(',', '.').replace(/[^0-9.]/g, '');
    if (cleaned === '') return null;
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
};

// Visina u centimetre: ako je uneto npr. 1.83 (metri), pretvara u 183.
const heightToCm = (value) => {
    const n = toNumber(value);
    if (n === null) return null;
    return n < 3 ? n * 100 : n;   // manje od 3 => metri
};

// Godine iz datuma rođenja (GGGG-MM-DD ili DD.MM.GGGG). Vraća null ako ne može.
export const ageFromBirthday = (birthday, now) => {
    if (!birthday) return null;
    const text = String(birthday).trim();
    let year, month, day;

    let m = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) { year = +m[1]; month = +m[2]; day = +m[3]; }
    else {
        m = text.match(/^(\d{1,2})[.\/-](\d{1,2})[.\/-](\d{4})/);
        if (m) { day = +m[1]; month = +m[2]; year = +m[3]; }
        else return null;
    }

    const today = now instanceof Date ? now : new Date();
    let age = today.getFullYear() - year;
    const beforeBirthday =
        today.getMonth() + 1 < month ||
        (today.getMonth() + 1 === month && today.getDate() < day);
    if (beforeBirthday) age -= 1;
    return age >= 0 && age < 130 ? age : null;
};

// Da li je pol ženski (prihvata "Ženski", "z", "žensko"...)
const isFemale = (gender) => {
    const g = String(gender || '').trim().toLowerCase();
    return g.startsWith('ž') || g.startsWith('z');
};

// BMI zaokružen na jednu decimalu; null ako fali podatak
export const calcBmi = (weight, height) => {
    const kg = toNumber(weight);
    const cm = heightToCm(height);
    if (kg === null || cm === null || cm === 0) return null;
    const m = cm / 100;
    return Math.round((kg / (m * m)) * 10) / 10;
};

// Kratak opis BMI kategorije
export const bmiCategory = (bmi) => {
    if (bmi === null || bmi === undefined) return '';
    if (bmi < 18.5) return 'pothranjenost';
    if (bmi < 25) return 'zdrava telesna masa';
    if (bmi < 30) return 'prekomerna telesna masa';
    return 'gojaznost';
};

// BMR (Mifflin-St Jeor), zaokružen na ceo broj; null ako fali podatak
export const calcBmr = (weight, height, age, gender) => {
    const kg = toNumber(weight);
    const cm = heightToCm(height);
    const yrs = toNumber(age);
    if (kg === null || cm === null || yrs === null) return null;
    const base = 10 * kg + 6.25 * cm - 5 * yrs;
    const bmr = base + (isFemale(gender) ? -161 : 5);
    return Math.round(bmr);
};

// TDEE = BMR * faktor; null ako fali podatak
export const calcTdee = (bmr, activityFactor) => {
    const f = toNumber(activityFactor);
    if (bmr === null || bmr === undefined || f === null) return null;
    return Math.round(bmr * f);
};

// Sve tri odjednom, na osnovu ulaza
export const calcAll = ({ weight, height, age, gender, activityFactor }) => {
    const bmi = calcBmi(weight, height);
    const bmr = calcBmr(weight, height, age, gender);
    const tdee = calcTdee(bmr, activityFactor);
    return { bmi, bmr, tdee, category: bmiCategory(bmi) };
};
